import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getProjectList,
  getPOList,
  uploadDirectInvoice,
} from "../../actions/project-management.action";
import { getProjectSummaryData } from "../../routes/projects/utils/get-project-summary";
import { getVendorList, addVendor } from "../../actions/vendor-management.action";
import { successSnackBar, warningSnackBar } from "../../actions/common.action";
import Modal from "../modal/modal";
import { Grid, Button, IconButton } from "@material-ui/core";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import StageTaskSelector from "../stage-task-selector";
import { toDecimal, roundValue } from "../../util/decimal-utils";
import { toUSD } from "../../util/currency-formatter";
import axios, { microServices } from "../../api/axios";

export default function UploadCostModal({ open, onClose, projectId: initialProjectId, defaultType, onSaveSuccess }) {
  const dispatch = useDispatch();
  const projectSummary = useSelector((state) => state.projectSummary);
  const navigate = useNavigate();
  const location = useLocation();

  // Form fields
  const [projectId, setProjectId] = useState(initialProjectId || "");
  const [costType, setCostType] = useState(defaultType || "");
  const [vendorId, setVendorId] = useState("");
  const [poId, setPoId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [attachment, setAttachment] = useState(null);

  // Line items state
  const [lineItems, setLineItems] = useState([]);

  // Master lists
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [budgetStructure, setBudgetStructure] = useState([]); // Raw hierarchical structure for modal
  const [flatTasksMap, setFlatTasksMap] = useState({}); // To resolve taskId to name quickly

  // Task selector popup state
  const [taskSelectorOpen, setTaskSelectorOpen] = useState(false);
  const [activeRowIndex, setActiveRowIndex] = useState(null);

  // Quick Add Vendor modal
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [newVendorType, setNewVendorType] = useState("Supplier");

  // Loading states
  const [loading, setLoading] = useState(false);

  // Load Projects and Vendors
  useEffect(() => {
    if (!open) return;

    // Load active projects only if not already on project details page
    if (initialProjectId) {
      const pName = projectSummary?.project?.projectName || projectSummary?.project?.name;
      if (pName) {
        setProjects([{ key: pName, value: initialProjectId }]);
      } else {
        axios('get', `/project?id=${initialProjectId}`, {}, { server: microServices.DE_CORE })
          .then((res) => {
            if (res && res.name) {
              setProjects([{ key: res.name, value: initialProjectId }]);
            } else {
              setProjects([{ key: "Current Project", value: initialProjectId }]);
            }
          })
          .catch(() => {
            setProjects([{ key: "Current Project", value: initialProjectId }]);
          });
      }
    } else {
      dispatch(getProjectList(null, "PROJECT")).then((res) => {
        if (Array.isArray(res)) {
          const filtered = res.filter(p => p.status !== "TENDER_PENDING" && p.status !== "TENDER_SUBMITTED" && p.status !== "COSTED" && p.status !== "LOI");
          setProjects(filtered.map(p => ({ key: p.name, value: p.id })));
        }
      }).catch(err => {
        console.error("Error loading projects:", err);
      });
    }

    // Load all vendors (suppliers and sub-contractors) robustly
    Promise.all([
      dispatch(getVendorList("Supplier", "ACTIVE")).catch(err => {
        console.error("Error loading suppliers:", err);
        return [];
      }),
      dispatch(getVendorList("sub-contractor", "ACTIVE")).catch(err => {
        console.error("Error loading sub-contractors:", err);
        return [];
      })
    ]).then(([suppliersRes, contractorsRes]) => {
      const merged = [];
      if (Array.isArray(suppliersRes)) {
        suppliersRes.forEach(v => merged.push({ key: v.businessName || v.name, value: v.id.toString(), type: "Supplier" }));
      }
      if (Array.isArray(contractorsRes)) {
        contractorsRes.forEach(v => merged.push({ key: v.businessName || v.name, value: v.id.toString(), type: "sub-contractor" }));
      }
      setVendors(merged);
    }).catch(err => {
      console.error("Error merging vendors:", err);
    });
  }, [open, initialProjectId, projectSummary, dispatch]);

  // Prefill state if coming back from Add Supplier/Contractor
  useEffect(() => {
    if (open && location.state?.openUploadCostModal) {
      if (location.state.projectId) setProjectId(location.state.projectId);
      if (location.state.costType) setCostType(location.state.costType);
      if (location.state.poId) setPoId(location.state.poId);
      if (location.state.invoiceNumber) setInvoiceNumber(location.state.invoiceNumber);
      if (location.state.lineItems) setLineItems(location.state.lineItems);
      if (location.state.newVendorId) setVendorId(location.state.newVendorId);
      
      // Clear location state
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [open, location.state]);

  // Prefill project ID if provided
  useEffect(() => {
    if (initialProjectId && !location.state?.openUploadCostModal) {
      setProjectId(initialProjectId);
    }
  }, [initialProjectId, open, location.state]);

  // Prefill cost type based on active tab
  useEffect(() => {
    if (open && !location.state?.openUploadCostModal) {
      setCostType(defaultType || "");
    }
  }, [open, defaultType, location.state]);

  // Load tasks and options when project changes
  useEffect(() => {
    if (!projectId) {
      setBudgetStructure([]);
      setFlatTasksMap({});
      return;
    }

    const isProject = projectSummary?.project?.projectType === "PROJECT" || projectSummary?.project?.projectDataType === "PROJECT";
    const label = isProject ? "Variations" : "Options";

    // Check if the project is the currently loaded project in Redux store
    if (projectSummary && projectSummary.project && projectSummary.project.projectId === projectId && Array.isArray(projectSummary.sectionData) && projectSummary.sectionData.length > 0) {
      const stagesList = JSON.parse(JSON.stringify(projectSummary.sectionData));
      const optionsList = isProject ? (projectSummary.variationData || []) : (projectSummary.optionData || []);
      const flatMap = {};

      const flatten = (stagesData, prefix = "") => {
        if (!Array.isArray(stagesData)) return;
        stagesData.forEach(stage => {
          if (stage.exclude_from_field_ops === true) return;
          const currentPrefix = prefix ? `${prefix} > ${stage.name}` : stage.name;
          if (stage.is_attribution_level === true) {
            flatMap[stage.id] = currentPrefix;
            return;
          }
          if (stage.tasks && Array.isArray(stage.tasks)) {
            stage.tasks.forEach(task => {
              flatMap[task.id] = `${currentPrefix} > ${task.name || 'Unnamed Task'}`;
            });
          }
          if (stage.stages && Array.isArray(stage.stages)) {
            flatten(stage.stages, currentPrefix);
          }
        });
      };

      flatten(stagesList);

      // Append variation options under a virtual stage so they are selectable
      if (optionsList.length > 0) {
        const virtualOptionStage = {
          id: "virtual-variations-options",
          name: label,
          stages: [],
          tasks: optionsList.map(opt => ({
            id: opt.id,
            name: opt.name || `Unnamed ${label.slice(0, -1)}`
          }))
        };
        stagesList.push(virtualOptionStage);
        optionsList.forEach(opt => {
          flatMap[opt.id] = `${label} > ${opt.name || `Unnamed ${label.slice(0, -1)}`}`;
        });
      }

      setBudgetStructure(stagesList);
      setFlatTasksMap(flatMap);
      return;
    }

    // If project mismatch or not loaded, fetch it to load it into Redux
    getProjectSummaryData({ projectId, dispatch }).catch(err => {
      console.error("Error fetching project summary data:", err);
    });
  }, [projectId, projectSummary, dispatch]);


  // Fetch purchase orders when project, vendor, or vendors list changes
  useEffect(() => {
    if (!projectId || !vendorId) {
      setPurchaseOrders([]);
      return;
    }

    const chosenVendor = vendors.find(v => v.value.toString() === vendorId.toString());
    const apiType = chosenVendor?.type === "sub-contractor" ? "sub-contractor" : "Supplier";

    dispatch(getPOList({ projectId, vendorId, vendorType: apiType, tab: "active" })).then((res) => {
      if (res && Array.isArray(res.list)) {
        setPurchaseOrders(res.list.map(po => ({
          key: `${po.f_id || po.id} (${po.status || 'Active'})`,
          value: po.u_id
        })));
      }
    }).catch(err => {
      console.error("Error fetching POs for project/vendor:", err);
      setPurchaseOrders([]);
    });
  }, [projectId, vendorId, vendors, dispatch]);

  const handleAddLineItem = () => {
    setLineItems([...lineItems, { taskId: "", description: "", amount: "" }]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const calculateGrandTotal = () => {
    let sum = toDecimal(0);
    lineItems.forEach(item => {
      const val = parseFloat(item.amount) || 0;
      sum = sum.plus(toDecimal(val));
    });
    return roundValue(sum, 2);
  };

  // Quick Add Vendor Submission
  const handleQuickAddVendor = () => {
    if (!newVendorName.trim()) {
      dispatch(warningSnackBar("Vendor Name is required"));
      return;
    }

    const vType = costType === "Material" ? "Supplier" : "sub-contractor";

    dispatch(addVendor({
      businessName: newVendorName.trim(),
      type: vType,
      status: "Active"
    })).then((createdVendor) => {
      if (createdVendor && createdVendor.id) {
        const newOption = {
          key: createdVendor.businessName || createdVendor.name,
          value: createdVendor.id.toString(),
          type: createdVendor.type
        };
        setVendors(prev => [newOption, ...prev]);
        setVendorId(createdVendor.id.toString());
        setQuickAddOpen(false);
        setNewVendorName("");
        dispatch(successSnackBar("Vendor added successfully"));
      }
    });
  };

  const handleSubmit = async () => {
    if (!projectId) {
      dispatch(warningSnackBar("Please select a Project"));
      return;
    }
    if (!costType) {
      dispatch(warningSnackBar("Please select a Type"));
      return;
    }
    if (!vendorId) {
      dispatch(warningSnackBar("Please select a Vendor"));
      return;
    }

    // Validate line items
    if (lineItems.length === 0) {
      dispatch(warningSnackBar("Please add at least one Line Item"));
      return;
    }

    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.taskId) {
        dispatch(warningSnackBar(`Please select a Task/Section for row ${i + 1}`));
        return;
      }
      if (!item.description.trim()) {
        dispatch(warningSnackBar(`Please enter a Description for row ${i + 1}`));
        return;
      }
      if (!item.amount || parseFloat(item.amount) <= 0) {
        dispatch(warningSnackBar(`Please enter a valid amount greater than 0 for row ${i + 1}`));
        return;
      }
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("projectId", projectId);
      formData.append("vendorId", vendorId);
      if (poId) {
        formData.append("poId", poId);
      }
      formData.append("invoiceNumber", invoiceNumber.trim() || `DCA-${Date.now()}`);
      formData.append("invoiceDate", new Date().toISOString());

      const invoiceClaim = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      formData.append("invoiceClaim", invoiceClaim);

      if (attachment) {
        formData.append("attachment", attachment);
      }

      lineItems.forEach((item) => {
        formData.append("items", JSON.stringify({
          taskId: item.taskId || null,
          type: costType,
          description: item.description,
          amount: parseFloat(item.amount)
        }));
      });

      await dispatch(uploadDirectInvoice(formData));

      dispatch(successSnackBar("Direct cost attributed successfully"));
      if (onSaveSuccess) onSaveSuccess();
      onClose();
    } catch (err) {
      console.error("Error submitting cost invoice:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTaskSelector = (index) => {
    if (!projectId) {
      dispatch(warningSnackBar("Please select a project first"));
      return;
    }
    setActiveRowIndex(index);
    setTaskSelectorOpen(true);
  };

  const handleSelectTask = (taskId) => {
    if (activeRowIndex !== null) {
      handleLineItemChange(activeRowIndex, "taskId", taskId);
    }
    setTaskSelectorOpen(false);
    setActiveRowIndex(null);
  };



  const filteredVendors = vendors.filter(v => {
    if (!costType) return false;
    if (costType === "Material") {
      return v.type === "Supplier";
    } else {
      return v.type === "sub-contractor";
    }
  });

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        modalHeading="ADD INVOICE"
        fullWidth
        maxWidth="lg"
        primary
        modalContent={
          <div className="p-3">
            {/* Top Row Fields */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <label className="input-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                  Select Project <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control-input"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  disabled={Boolean(initialProjectId)}
                  style={{ minHeight: "38px" }}
                >
                  <option value="" disabled>Select a project</option>
                  {projects.map(p => (
                    <option key={p.value} value={p.value}>{p.key}</option>
                  ))}
                </select>
              </Grid>

              <Grid item xs={12} sm={4}>
                <label className="input-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                  Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control-input"
                  value={costType}
                  onChange={(e) => {
                    setCostType(e.target.value);
                    setVendorId("");
                    setPoId("");
                  }}
                  style={{ minHeight: "38px" }}
                >
                  <option value="" disabled>Select Type</option>
                  <option value="Material">Material</option>
                  <option value="Contractor">Contractor</option>
                </select>
              </Grid>

              <Grid item xs={12} sm={4}>
                <label className="input-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                  Select Vendor <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center gap-1">
                  <select
                    className="form-control-input flex-grow-1"
                    value={vendorId}
                    onChange={(e) => setVendorId(e.target.value)}
                    disabled={!costType}
                    style={{ minHeight: "38px" }}
                  >
                    <option value="" disabled>{!costType ? "Select Type first" : "Select a vendor"}</option>
                    {filteredVendors.map(v => (
                      <option key={v.value} value={v.value}>{v.key}</option>
                    ))}
                  </select>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      let returnPath = location.pathname + location.search;
                      if (location.pathname.includes("vendor-payment") && projectId) {
                        returnPath = `/vendor-payment?project=${projectId}`;
                      }
                      const routeState = {
                        returnTo: returnPath,
                        projectId,
                        costType,
                        poId,
                        invoiceNumber,
                        lineItems,
                      };
                      if (costType === "Material") {
                        navigate("/vendor/add-supplier", { state: routeState });
                      } else if (costType === "Contractor") {
                        navigate("/vendor/add-contractor", { state: routeState });
                      }
                    }}
                    disabled={!costType}
                    style={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "7px" }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <label className="input-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                  Select Purchase Order
                </label>
                <select
                  className="form-control-input"
                  value={poId}
                  onChange={(e) => setPoId(e.target.value)}
                  disabled={!projectId || !vendorId}
                  style={{ minHeight: "38px" }}
                >
                  <option value="">{!projectId ? "Select project first" : !vendorId ? "Select vendor first" : "Select PO (Optional)"}</option>
                  {purchaseOrders.map(po => (
                    <option key={po.value} value={po.value}>{po.key}</option>
                  ))}
                </select>
              </Grid>

              <Grid item xs={12} sm={4}>
                <label className="input-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                  Invoice Number
                </label>
                <input
                  type="text"
                  className="form-control-input"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Optional – auto-generated if blank"
                  style={{ minHeight: "38px" }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <label className="input-label" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                  Invoice Document
                </label>
                <div
                  className="d-flex align-items-center justify-content-center border rounded py-1 px-2 text-muted"
                  style={{ minHeight: "38px", borderStyle: "dashed", cursor: "pointer", position: "relative" }}
                >
                  <AttachFileIcon fontSize="small" className="me-1" />
                  <span style={{ fontSize: "0.85rem" }}>
                    {attachment ? attachment.name : "Upload file"}
                  </span>
                  <input
                    type="file"
                    onChange={(e) => setAttachment(e.target.files[0] || null)}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
                  />
                </div>
              </Grid>
            </Grid>

            {/* Line Items Manual Grid Section */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 style={{ fontWeight: "600", fontSize: "1.1rem", margin: 0 }}>Line Items</h4>
                <Button
                  variant="contained"
                  onClick={handleAddLineItem}
                  disabled={!projectId}
                  style={{ backgroundColor: "#000", color: "#fff", textTransform: "none", borderRadius: "6px" }}
                >
                  + Add Line Item
                </Button>
              </div>

              {/* Grid Table */}
              <div className="table-responsive border rounded">
                <table className="table bordered mb-0" style={{ fontSize: "0.9rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "45%", fontWeight: "600" }}>Task/Section <span className="text-danger">*</span></th>
                      <th style={{ width: "40%", fontWeight: "600" }}>Description *</th>
                      <th style={{ width: "12%", fontWeight: "600" }}>Amount *</th>
                      <th style={{ width: "3%", textAlign: "center" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          No line items added. Click "Add Line Item" to begin.
                        </td>
                      </tr>
                    ) : (
                      lineItems.map((item, index) => (
                        <tr key={index}>
                          <td style={{ verticalAlign: "middle" }}>
                            {/* Clickable input wrapper triggers beautiful popup selector dialog */}
                            <div
                              onClick={() => handleOpenTaskSelector(index)}
                              className={`form-control-input py-1 d-flex align-items-center justify-content-between bg-white cursor-pointer ${!item.taskId ? 'text-muted' : 'text-dark'}`}
                              style={{ minHeight: "34px", border: `1px solid ${!item.taskId ? '#ced4da' : '#ced4da'}`, borderRadius: "4px" }}
                            >
                              <span className="text-dark" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {item.taskId ? flatTasksMap[item.taskId] : "Select Task/Section"}
                              </span>
                              <KeyboardArrowRightIcon fontSize="small" />
                            </div>
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <input
                              type="text"
                              className="form-control-input py-1"
                              value={item.description}
                              onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                              placeholder="Enter description"
                              required
                            />
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <input
                              type="number"
                              className="form-control-input py-1 text-right"
                              style={{ textAlign: "right" }}
                              value={item.amount}
                              onChange={(e) => handleLineItemChange(index, "amount", e.target.value)}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              required
                            />
                          </td>
                          <td align="center" style={{ verticalAlign: "middle" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveLineItem(index)}
                              style={{ color: "#dc3545" }}
                            >
                              <DeleteIcon style={{ fontSize: "20px" }} />
                            </IconButton>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Grand Total calculation */}
              {lineItems.length > 0 && (
                <div className="d-flex justify-content-end align-items-center mt-2">
                  <div style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
                    Grand Total: <span className="text-primary">{toUSD(calculateGrandTotal())}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
        modalContentStyle={{ paddingBottom: 0 }}
        showActionBtn
        modalSaveTxt={loading ? "SAVING..." : "SAVE INVOICE"}
        onClick={handleSubmit}
        modalCloseTxt="CANCEL"
      />

      {/* Hierarchical Accordion Task/Section Selector Dialog */}
      <Modal
        open={taskSelectorOpen}
        onClose={() => setTaskSelectorOpen(false)}
        modalHeading="Select Task/Section"
        maxWidth="sm"
        modalContent={
          <div className="p-3">
            <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
              Select a task to attribute this cost. Tasks marked as not allowing attribution are hidden.
            </p>
            <StageTaskSelector
              budgetStructure={budgetStructure}
              onSelectTask={handleSelectTask}
            />
          </div>
        }
        cancel="Cancel"
        onClick={() => setTaskSelectorOpen(false)}
      />

      {/* Quick Add Vendor Modal */}
      <Modal
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        modalHeading="Quick Add Vendor"
        maxWidth="sm"
        modalContent={
          <div className="p-3">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <label className="input-label mb-1">Vendor / Business Name *</label>
                <input
                  type="text"
                  className="form-control-input"
                  value={newVendorName}
                  onChange={(e) => setNewVendorName(e.target.value)}
                  placeholder="Business Name"
                  required
                />
              </Grid>
            </Grid>
          </div>
        }
        cancel="Cancel"
        save="Create Vendor"
        onClick={() => setQuickAddOpen(false)}
        onEdit={handleQuickAddVendor}
      />
    </>
  );
}
